import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Wifi, Printer, ChefHat, Wine, Pizza, Layers, Usb, Bluetooth, CheckCircle2, AlertTriangle, Tv, RefreshCw, Check } from "lucide-react";
import { webusb, webbt } from "@/lib/browserPrinting";
import HealthDiagnostics from "@/components/HealthDiagnostics";

const MAX_COMMAND = 4;

const DEST_META = {
    kitchen: { label: "Cucina", icon: ChefHat, color: "text-orange-300", border: "border-orange-500/40", bg: "bg-orange-900/15" },
    bar:     { label: "Bar",     icon: Wine,    color: "text-cyan-300",   border: "border-cyan-500/40",   bg: "bg-cyan-900/15" },
    pizza:   { label: "Pizzeria",icon: Pizza,   color: "text-red-300",    border: "border-red-500/40",    bg: "bg-red-900/15" },
    generic: { label: "Generica",icon: Layers,  color: "text-elegant",    border: "border-[#1f2c44]",     bg: "bg-deep" },
};

const PROTOCOL_META = {
    tcp:       { label: "Rete (LAN)", icon: Wifi, color: "text-emerald-300", hint: "Stampa diretta dal cloud — sempre attiva" },
    usb:       { label: "USB",        icon: Usb, color: "text-purple-300",  hint: "Pairing dal browser (Chrome/Edge sul PC con la stampante)" },
    bluetooth: { label: "Bluetooth",  icon: Bluetooth, color: "text-sky-300", hint: "Pairing dal browser (Chrome/Edge sul dispositivo con la stampante)" },
};

const Stampanti = () => {
    const [tab, setTab] = useState("command");

    // ---------- FISCAL ----------
    const [printers, setPrinters] = useState([]);
    const [models, setModels] = useState([]);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState({ name: "", brand: "Epson", model: "FP-81II RT", ip: "", port: "9100", protocol: "tcp", active: false });

    // ---------- COMMAND ----------
    const [cprinters, setCprinters] = useState([]);
    const [cmodels, setCmodels] = useState([]);
    const [cshow, setCshow] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const blankCmd = {
        name: "", destination: "kitchen", destinations: [], brand: "Epson", model: "TM-T20III",
        protocol: "tcp", ip: "", port: "9100",
        usb_vendor_id: null, usb_product_id: null, usb_product_name: "",
        bt_device_id: "", bt_device_name: "",
        paper_width: 80, active: true,
        is_universal: false,
    };
    const [cform, setCform] = useState(blankCmd);

    // ---------- STATION MODES ----------
    const [stationModes, setStationModes] = useState({ kitchen: "video_only", bar: "video_only", pizza: "video_only" });
    const loadModes = async () => {
        try { const { data } = await api.get("/station-settings"); setStationModes(data); }
        catch (_e) { /* */ }
    };
    useEffect(() => { loadModes(); }, []);
    const changeStationMode = async (station, mode) => {
        try {
            await api.patch(`/station-settings/${station}`, { mode });
            setStationModes((s) => ({ ...s, [station]: mode }));
            toast.success(`Modalità ${station} → ${mode}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        }
    };

    const loadFiscal = async () => {
        const [p, m] = await Promise.all([api.get("/printers"), api.get("/printers/models")]);
        setPrinters(p.data); setModels(m.data);
    };
    const loadCommand = async () => {
        const [p, m] = await Promise.all([api.get("/command-printers"), api.get("/command-printers/models")]);
        setCprinters(p.data); setCmodels(m.data);
    };
    useEffect(() => { loadFiscal(); loadCommand(); }, []);

    // ---------- Stato pairing (paired_in_browser) per ogni stampante USB/BT ----------
    const [pairedMap, setPairedMap] = useState({}); // {printerId: true/false}
    const checkPaired = useCallback(async () => {
        const map = {};
        for (const p of cprinters) {
            const proto = (p.protocol || "").toLowerCase();
            try {
                if (proto === "usb" && p.usb_vendor_id && p.usb_product_id) {
                    const d = webusb.supported() ? await webusb.findPairedDevice(p.usb_vendor_id, p.usb_product_id) : null;
                    map[p.id] = !!d;
                } else if (proto === "bluetooth" && p.bt_device_id) {
                    const d = webbt.supported() ? await webbt.findPairedDevice(p.bt_device_id) : null;
                    map[p.id] = !!d;
                } else {
                    map[p.id] = null; // TCP: stato non applicabile
                }
            } catch (_e) { map[p.id] = false; }
        }
        setPairedMap(map);
    }, [cprinters]);

    useEffect(() => {
        if (cprinters.length === 0) return;
        checkPaired();
        const id = setInterval(checkPaired, 30000); // auto-retry ogni 30s
        return () => clearInterval(id);
    }, [cprinters, checkPaired]);

    const reconnectAll = async () => {
        toast.info("Verifico le stampanti…");
        await checkPaired();
        // Riapri il modale globale forzando un nuovo ciclo
        sessionStorage.removeItem("smarttable_printers_reconnect_shown_v1");
        // Forza il PrinterAutoConnect a ricontrollare ricaricando il context user
        window.dispatchEvent(new CustomEvent("smarttable:reconnect-printers"));
        toast.success("Stato stampanti aggiornato");
    };

    // ---------- FISCAL handlers ----------
    const createFiscal = async () => {
        try { await api.post("/printers", form); toast.success("Stampante aggiunta"); setShow(false); loadFiscal(); }
        catch (e) { toast.error(formatError(e)); }
    };
    const testFiscal = async (id) => {
        try { const { data } = await api.post(`/printers/${id}/test`); toast.success(data.message); }
        catch (e) { toast.error(formatError(e)); }
    };
    const removeFiscal = async (id) => {
        if (!window.confirm("Eliminare la stampante?")) return;
        await api.delete(`/printers/${id}`); loadFiscal();
    };
    const brandModels = models.filter((m) => m.brand === form.brand);

    // ---------- COMMAND handlers ----------
    const cBrandModels = useMemo(() => cmodels.filter((m) => m.brand === cform.brand), [cmodels, cform.brand]);

    const openCreate = () => {
        if (cprinters.length >= MAX_COMMAND) {
            toast.error(`Limite massimo: ${MAX_COMMAND} stampanti commesse`);
            return;
        }
        setEditingId(null); setCform(blankCmd); setCshow(true);
    };

    // Apri il form con il flag UNIVERSALE già attivato (1 stampante per tutti i reparti)
    const openCreateUniversal = () => {
        const existingUniversal = cprinters.find((p) => p.is_universal);
        if (existingUniversal) {
            // Modifica quella esistente invece di crearne una nuova
            openEdit(existingUniversal);
            toast.info(`Stampante universale già configurata: ${existingUniversal.name}`);
            return;
        }
        if (cprinters.length >= MAX_COMMAND) {
            toast.error(`Limite massimo: ${MAX_COMMAND} stampanti commesse`);
            return;
        }
        setEditingId(null);
        setCform({ ...blankCmd, name: "Stampante Universale", is_universal: true });
        setCshow(true);
    };
    const openEdit = (p) => {
        setEditingId(p.id);
        setCform({
            name: p.name || "",
            destination: p.destination || "kitchen",
            destinations: Array.isArray(p.destinations) ? p.destinations : [],
            brand: p.brand, model: p.model,
            protocol: p.protocol || "tcp",
            ip: p.ip || "", port: p.port || "9100",
            usb_vendor_id: p.usb_vendor_id || null,
            usb_product_id: p.usb_product_id || null,
            usb_product_name: p.usb_product_name || "",
            bt_device_id: p.bt_device_id || "",
            bt_device_name: p.bt_device_name || "",
            paper_width: p.paper_width || 80,
            active: p.active !== false,
            is_universal: p.is_universal === true,
        });
        setCshow(true);
    };
    const saveCommand = async () => {
        if (!cform.name.trim()) { toast.error("Assegna un nome alla stampante"); return; }
        if (cform.protocol === "tcp" && !cform.ip.trim()) { toast.error("Inserisci l'IP della stampante di rete"); return; }
        if (cform.protocol === "usb" && !cform.usb_vendor_id) { toast.error("Esegui prima il pairing USB col pulsante 'Collega'"); return; }
        if (cform.protocol === "bluetooth" && !cform.bt_device_id) { toast.error("Esegui prima il pairing Bluetooth col pulsante 'Collega'"); return; }
        try {
            if (editingId) {
                await api.patch(`/command-printers/${editingId}`, cform);
                toast.success("Stampante aggiornata");
            } else {
                await api.post("/command-printers", cform);
                toast.success("Stampante aggiunta");
            }
            setCshow(false); setEditingId(null); loadCommand();
        } catch (e) { toast.error(formatError(e)); }
    };
    const testCommand = async (id) => {
        try { const { data } = await api.post(`/command-printers/${id}/test`); toast.success(data.message); }
        catch (e) { toast.error(formatError(e)); }
    };

    /** Test diretto USB/BT da questo browser (bypassa il giro websocket per debug rapido). */
    const testDirect = async (p) => {
        const testLines = [
            { text: "TEST DIRETTO BROWSER", size: "large", align: "center", bold: true },
            { text: p.name, size: "normal", align: "center" },
            { text: `${p.brand} ${p.model}`, size: "small", align: "center" },
            { text: "----------------", size: "small", align: "center" },
            { text: "Se leggi questo testo,", size: "normal", align: "center" },
            { text: "la stampante e' pairata", size: "normal", align: "center" },
            { text: "correttamente!", size: "normal", align: "center" },
        ];
        const job = { lines: testLines, printer_name: p.name };
        try {
            if (p.protocol === "usb") {
                if (!webusb.supported()) throw new Error("Chrome/Edge richiesti");
                const dev = await webusb.findPairedDevice(p.usb_vendor_id, p.usb_product_id);
                if (!dev) throw new Error(
                    "Stampante non pairata in QUESTO browser. Devi fare il pairing dalla stessa scheda Chrome del PC con la stampante (Modifica → Cambia)."
                );
                await webusb.printJob(job, p.usb_vendor_id, p.usb_product_id);
                toast.success("Stampato!");
            } else if (p.protocol === "bluetooth") {
                if (!webbt.supported()) throw new Error("Chrome/Edge richiesti");
                const dev = await webbt.findPairedDevice(p.bt_device_id);
                if (!dev) throw new Error("Stampante BT non pairata in questo browser");
                await webbt.printJob(job, p.bt_device_id);
                toast.success("Stampato!");
            }
        } catch (e) {
            console.error("[Test diretto] errore:", e);
            toast.error(`Errore: ${e.message}`, { duration: 8000 });
        }
    };
    const removeCommand = async (p) => {
        if (!window.confirm(`Eliminare la stampante "${p.name}"?`)) return;
        try { await api.delete(`/command-printers/${p.id}`); loadCommand(); toast.success("Eliminata"); }
        catch (e) { toast.error(formatError(e)); }
    };
    const toggleActive = async (p) => {
        try { await api.patch(`/command-printers/${p.id}`, { active: !p.active }); loadCommand(); }
        catch (e) { toast.error(formatError(e)); }
    };

    // ---------- USB / Bluetooth pairing ----------
    const pairUsb = async () => {
        if (!webusb.supported()) {
            toast.error("WebUSB non supportato. Apri Smart Table in Chrome o Edge.");
            return;
        }
        try {
            const dev = await webusb.requestDevice();
            setCform({
                ...cform,
                usb_vendor_id: dev.vendorId,
                usb_product_id: dev.productId,
                usb_product_name: dev.productName || `${dev.manufacturerName || ""} ${dev.productName || ""}`.trim(),
            });
            toast.success(`USB collegata: ${dev.productName || `${dev.vendorId.toString(16)}:${dev.productId.toString(16)}`}`);
        } catch (e) {
            if (e.name !== "NotFoundError") {
                toast.error("Pairing USB fallito: " + e.message);
            }
        }
    };
    const pairBt = async () => {
        if (!webbt.supported()) {
            toast.error("Web Bluetooth non supportato. Apri Smart Table in Chrome o Edge.");
            return;
        }
        try {
            const dev = await webbt.requestDevice();
            setCform({
                ...cform,
                bt_device_id: dev.id,
                bt_device_name: dev.name,
            });
            toast.success(`Bluetooth collegato: ${dev.name || dev.id}`);
        } catch (e) {
            if (e.name !== "NotFoundError") {
                toast.error("Pairing Bluetooth fallito: " + e.message);
            }
        }
    };

    const renderPairBadge = (p) => {
        if (p.protocol === "tcp") {
            return p.ip
                ? <span className="text-[10px] text-emerald-300 flex items-center gap-1" data-testid={`pair-badge-${p.id}`}><Wifi className="w-3 h-3" /> {p.ip}:{p.port || 9100}</span>
                : <span className="text-[10px] text-amber-300 flex items-center gap-1" data-testid={`pair-badge-${p.id}`}><AlertTriangle className="w-3 h-3" /> IP mancante</span>;
        }
        // USB / Bluetooth — badge live in base allo stato di pairedMap (verde = trovata in questo browser ora)
        const isUsb = p.protocol === "usb";
        const isBt = p.protocol === "bluetooth";
        const hasIds = isUsb ? !!p.usb_vendor_id : (isBt ? !!p.bt_device_id : false);
        if (!hasIds) {
            return <span className="text-[10px] text-amber-300 flex items-center gap-1" data-testid={`pair-badge-${p.id}`}><AlertTriangle className="w-3 h-3" /> Pairing mancante</span>;
        }
        const live = pairedMap[p.id];
        if (live === true) {
            return <span className="text-[10px] text-green-300 flex items-center gap-1 font-bold" data-testid={`pair-badge-${p.id}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> Pronta · {isUsb ? "USB" : "BT"}
            </span>;
        }
        if (live === false) {
            return <span className="text-[10px] text-amber-300 flex items-center gap-1" data-testid={`pair-badge-${p.id}`}>
                <AlertTriangle className="w-3 h-3" /> Da collegare a questo PC
            </span>;
        }
        // live === undefined: in caricamento
        return <span className="text-[10px] text-elegant flex items-center gap-1" data-testid={`pair-badge-${p.id}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-elegant" /> Verifico…
        </span>;
    };

    return (
        <div className="space-y-6" data-testid="stampanti-page">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Hardware Stampa</div>
                    <h1 className="font-display text-4xl font-bold">Stampanti</h1>
                </div>
            </div>

            {/* Tabs */}
            <div className="inline-flex bg-surface border border-[#1f2c44] rounded-lg p-1" role="tablist">
                <button role="tab" onClick={() => setTab("command")} data-testid="tab-command"
                    className={`h-10 px-5 rounded-md text-xs uppercase tracking-wider font-bold flex items-center gap-2 transition-colors ${
                        tab === "command" ? "bg-brand text-deep" : "text-elegant hover:text-warm"
                    }`}>
                    <Printer className="w-4 h-4" /> Commesse
                    <span className={`ml-1 px-2 py-0.5 rounded text-[10px] ${tab === "command" ? "bg-deep/30 text-deep" : "bg-deep text-elegant"}`}>
                        {cprinters.length}/{MAX_COMMAND}
                    </span>
                </button>
                <button role="tab" onClick={() => setTab("fiscal")} data-testid="tab-fiscal"
                    className={`h-10 px-5 rounded-md text-xs uppercase tracking-wider font-bold flex items-center gap-2 transition-colors ${
                        tab === "fiscal" ? "bg-brand text-deep" : "text-elegant hover:text-warm"
                    }`}>
                    Telematiche / Fiscali
                </button>
            </div>

            {/* ============ TAB COMMAND ============ */}
            {tab === "command" && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <p className="text-sm text-elegant">
                            Stampanti termiche <span className="text-warm font-bold">non fiscali</span> per le commesse. Connessione <strong>USB</strong>, <strong>LAN</strong> o <strong>Bluetooth</strong>. Massimo <strong>{MAX_COMMAND}</strong>.
                        </p>
                        <div className="flex gap-2 flex-wrap">
                            {cprinters.some((p) => ["usb","bluetooth"].includes(p.protocol)) && (
                                <button onClick={reconnectAll} data-testid="reconnect-all-button"
                                    title="Riverifica lo stato di tutte le stampanti USB/BT in questo browser"
                                    className="h-12 px-4 border border-brand text-brand rounded-md flex items-center gap-2 hover:bg-brand/10 text-xs uppercase tracking-wider font-bold">
                                    <RefreshCw className="w-4 h-4" /> Riconnetti tutto
                                </button>
                            )}
                            <button onClick={openCreateUniversal} data-testid="add-universal-printer-button"
                                disabled={cprinters.length >= MAX_COMMAND && !cprinters.some(p => p.is_universal)}
                                title="Configura UNA stampante per tutti i reparti (cucina, bar, pizza)"
                                className="h-12 px-5 border-2 border-brand bg-brand/10 text-brand font-bold rounded-md flex items-center gap-2 disabled:opacity-40 hover:bg-brand/20 text-xs uppercase tracking-wider">
                                <Printer className="w-4 h-4" /> Stampante Unica
                            </button>
                            <button onClick={openCreate} data-testid="add-command-printer-button"
                                disabled={cprinters.length >= MAX_COMMAND}
                                className="h-12 px-5 bg-brand text-deep font-bold rounded-md flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed text-xs uppercase tracking-wider">
                                <Plus className="w-4 h-4" /> Aggiungi
                            </button>
                        </div>
                    </div>

                    {/* Info banner — spiega come funziona la stampa nei 3 canali */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {Object.entries(PROTOCOL_META).map(([k, m]) => {
                            const Icon = m.icon;
                            return (
                                <div key={k} className="bg-surface border border-[#1f2c44] rounded-lg p-3 flex items-start gap-3">
                                    <div className="w-9 h-9 rounded-md bg-deep border border-[#1f2c44] flex items-center justify-center flex-shrink-0">
                                        <Icon className={`w-4 h-4 ${m.color}`} />
                                    </div>
                                    <div className="text-xs">
                                        <div className="font-bold text-warm">{m.label}</div>
                                        <div className="text-elegant">{m.hint}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Diagnostica sistema */}
                    <HealthDiagnostics />

                    {/* Station mode panels */}
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-5">
                        <div className="font-display text-lg font-bold mb-1">Modalità delle stazioni</div>
                        <p className="text-xs text-elegant mb-4">
                            Scegli come gestire le comande per ogni stazione. <strong>Solo stampante</strong>: item subito pronti, niente video. <strong>Misto</strong>: stampa + video. <strong>Solo video</strong>: solo KDS (default).
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {[
                                { key: "kitchen", label: "Cucina", icon: ChefHat, color: "text-orange-300" },
                                { key: "bar", label: "Bar", icon: Wine, color: "text-cyan-300" },
                                { key: "pizza", label: "Pizzeria", icon: Pizza, color: "text-red-300" },
                            ].map((s) => {
                                const Icon = s.icon;
                                const current = stationModes[s.key] || "video_only";
                                return (
                                    <div key={s.key} className="bg-deep border border-[#1f2c44] rounded-md p-3" data-testid={`station-mode-card-${s.key}`}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <Icon className={`w-4 h-4 ${s.color}`} />
                                            <div className="font-bold text-warm text-sm">{s.label}</div>
                                        </div>
                                        <div className="grid grid-cols-3 gap-1">
                                            {[
                                                { v: "mixed", lbl: "Misto", Ic: Layers },
                                                { v: "video_only", lbl: "Video", Ic: Tv },
                                            ].map(({ v, lbl, Ic }) => {
                                                const active = current === v;
                                                return (
                                                    <button key={v} onClick={() => changeStationMode(s.key, v)}
                                                        data-testid={`set-${s.key}-${v}`}
                                                        className={`h-12 rounded text-[10px] uppercase tracking-wider font-bold flex flex-col items-center justify-center gap-0.5 border ${
                                                            active ? "bg-brand text-deep border-brand" : "border-[#1f2c44] text-elegant hover:border-brand/50"
                                                        }`}>
                                                        <Ic className="w-3 h-3" />
                                                        {lbl}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {cprinters.map((p) => {
                            // Multi-dest: usa la prima destinazione attiva per il meta visuale, altrimenti `destination` legacy
                            const activeDests = (p.destinations && p.destinations.length > 0) ? p.destinations : [p.destination || "generic"];
                            const meta = DEST_META[activeDests[0]] || DEST_META.generic;
                            const Icon = meta.icon;
                            const ProtoIcon = PROTOCOL_META[p.protocol]?.icon || Wifi;
                            const isMulti = !p.is_universal && activeDests.length > 1;
                            return (
                                <div key={p.id} className={`bg-surface border-2 ${meta.border} rounded-lg p-5`} data-testid={`command-printer-${p.id}`}>
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-11 h-11 rounded-md ${meta.bg} border ${meta.border} flex items-center justify-center`}>
                                                <Icon className={`w-5 h-5 ${meta.color}`} />
                                            </div>
                                            <div>
                                                <div className={`text-[10px] uppercase tracking-[0.2em] ${meta.color}`}>
                                                    {p.is_universal
                                                        ? "UNIVERSALE · Tutte le comande"
                                                        : isMulti
                                                            ? `MULTI · ${activeDests.map((d) => (DEST_META[d]?.label || d)).join(" + ")}`
                                                            : meta.label}
                                                </div>
                                                <div className="font-display text-xl font-bold leading-tight">{p.name}</div>
                                            </div>
                                        </div>
                                        <button onClick={() => toggleActive(p)} data-testid={`toggle-active-${p.id}`}
                                            title={p.active ? "Attiva" : "Disattivata"}
                                            className={`h-6 px-2 rounded text-[10px] font-bold uppercase tracking-wider ${
                                                p.active ? "bg-green-900/40 text-green-300 border border-green-500/50" : "bg-red-900/30 text-red-300 border border-red-500/40"
                                            }`}>
                                            {p.active ? "On" : "Off"}
                                        </button>
                                    </div>

                                    <div className="text-xs space-y-1 text-elegant">
                                        <div className="flex items-center gap-2">
                                            <ProtoIcon className="w-3 h-3" />
                                            <span className="text-warm font-medium">{PROTOCOL_META[p.protocol]?.label || p.protocol}</span>
                                            <span>·</span>
                                            <span>{p.brand} {p.model}</span>
                                        </div>
                                        <div>Carta {p.paper_width}mm</div>
                                        {renderPairBadge(p)}
                                    </div>

                                    <div className="flex gap-2 mt-4">
                                        <button onClick={() => testCommand(p.id)} data-testid={`test-command-${p.id}`}
                                            title="Stampa via canale principale (cloud → WebSocket → browser)"
                                            className="flex-1 h-10 border border-brand text-brand rounded-md text-xs uppercase tracking-wider flex items-center justify-center gap-1 hover:bg-brand/10">
                                            <Wifi className="w-3 h-3" /> Test
                                        </button>
                                        {(p.protocol === "usb" || p.protocol === "bluetooth") && (
                                            <button onClick={() => testDirect(p)} data-testid={`test-direct-${p.id}`}
                                                title="Stampa direttamente dal browser corrente (mostra errori dettagliati)"
                                                className="h-10 px-3 border border-purple-400/60 text-purple-200 hover:bg-purple-900/30 rounded-md text-[10px] uppercase tracking-wider flex items-center gap-1">
                                                Test diretto
                                            </button>
                                        )}
                                        <button onClick={() => openEdit(p)} data-testid={`edit-command-${p.id}`}
                                            className="h-10 px-3 border border-[#1f2c44] text-elegant rounded-md text-xs uppercase tracking-wider hover:border-brand hover:text-brand">
                                            Modifica
                                        </button>
                                        <button onClick={() => removeCommand(p)} data-testid={`delete-command-${p.id}`}
                                            className="w-10 h-10 border border-red-700/40 text-red-400 rounded-md hover:bg-red-900/20">
                                            <X className="w-4 h-4 mx-auto" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                        {cprinters.length === 0 && (
                            <div className="col-span-full bg-surface border border-dashed border-[#1f2c44] rounded-lg p-10 text-center" data-testid="command-printers-empty">
                                <Printer className="w-10 h-10 mx-auto text-elegant mb-3" />
                                <p className="text-elegant mb-1">Nessuna stampante di commessa configurata.</p>
                                <p className="text-xs text-elegant">Aggiungi fino a {MAX_COMMAND} stampanti termiche per smistare le comande.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ============ MODAL COMMAND ============ */}
            {cshow && (
                <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4" onClick={() => setCshow(false)}>
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 max-w-lg w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="command-printer-modal">
                        <h2 className="font-display text-2xl font-bold mb-4">
                            {editingId ? "Modifica stampante" : "Nuova stampante di commessa"}
                        </h2>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-[10px] uppercase tracking-wider text-elegant mb-1">Nome (obbligatorio)</label>
                                <input className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                                    placeholder="Es. Cucina, Bar, Pizzeria..."
                                    value={cform.name}
                                    onChange={(e) => setCform({ ...cform, name: e.target.value })}
                                    data-testid="cprinter-name" />
                            </div>

                            <div>
                                <label className="block text-[10px] uppercase tracking-wider text-elegant mb-1">
                                    Destinazioni — quali comande deve stampare?
                                </label>
                                <div className="text-[10px] text-elegant mb-2">
                                    Seleziona <strong>una o più</strong> destinazioni. Se non selezioni nulla, la stampante non riceverà comande.
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {Object.entries(DEST_META).map(([k, m]) => {
                                        const Icon = m.icon;
                                        const dests = cform.destinations || [];
                                        const active = dests.includes(k);
                                        const disabled = cform.is_universal;
                                        const toggle = () => {
                                            if (disabled) return;
                                            const next = active ? dests.filter((x) => x !== k) : [...dests, k];
                                            // Mantieni `destination` per compat (primo della lista o kitchen)
                                            setCform({ ...cform, destinations: next, destination: next[0] || "kitchen" });
                                        };
                                        return (
                                            <button key={k} onClick={toggle} data-testid={`cprinter-dest-${k}`}
                                                disabled={disabled}
                                                className={`h-16 rounded-md border-2 flex flex-col items-center justify-center gap-1 text-[10px] uppercase tracking-wider relative ${
                                                    disabled ? "opacity-30 cursor-not-allowed border-[#1f2c44] text-elegant" :
                                                    active ? `${m.border} ${m.bg} ${m.color}` : "border-[#1f2c44] text-elegant hover:border-brand"
                                                }`}>
                                                {active && (
                                                    <span className="absolute top-1 right-1">
                                                        <Check className="w-3 h-3" />
                                                    </span>
                                                )}
                                                <Icon className="w-4 h-4" />
                                                {m.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                {(cform.destinations || []).length > 1 && !cform.is_universal && (
                                    <div className="text-[10px] text-amber-300 mt-2 flex items-center gap-1">
                                        ℹ️ Multi-destinazione: la stampante riceverà <strong>{cform.destinations.length} ticket separati</strong> per ordine ({cform.destinations.join(" + ")}).
                                    </div>
                                )}
                                {(cform.destinations || []).length === 0 && !cform.is_universal && (
                                    <div className="text-[10px] text-red-400 mt-2">
                                        ⚠️ Nessuna destinazione: questa stampante NON riceverà comande. Seleziona almeno una.
                                    </div>
                                )}
                            </div>

                            {/* ⚠️ Stampante UNIVERSALE: 1 sola stampante per kitchen+bar+pizza (3 ticket separati) */}
                            <div className="bg-brand/5 border border-brand/30 rounded-md p-3">
                                <label className="flex items-center gap-3 cursor-pointer" data-testid="cprinter-universal-toggle">
                                    <input type="checkbox" className="w-4 h-4 accent-brand"
                                        checked={cform.is_universal}
                                        onChange={(e) => setCform({ ...cform, is_universal: e.target.checked })} />
                                    <div className="flex-1">
                                        <div className="text-sm font-semibold text-warm">Stampante unica per tutte le comande</div>
                                        <div className="text-[11px] text-elegant mt-0.5">
                                            Una sola stampante fisica riceve TUTTE le comande (cucina, bar, pizza) come 3 ticket separati. Disabilita la scelta della destinazione.
                                        </div>
                                    </div>
                                </label>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] uppercase tracking-wider text-elegant mb-1">Marca</label>
                                    <select className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md"
                                        value={cform.brand}
                                        onChange={(e) => {
                                            const brand = e.target.value;
                                            const firstModel = cmodels.find((m) => m.brand === brand);
                                            setCform({ ...cform, brand, model: firstModel?.model || "", paper_width: firstModel?.width || cform.paper_width });
                                        }}
                                        data-testid="cprinter-brand">
                                        {[...new Set(cmodels.map((m) => m.brand))].map((b) => <option key={b} value={b}>{b}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase tracking-wider text-elegant mb-1">Modello</label>
                                    <select className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md"
                                        value={cform.model}
                                        onChange={(e) => {
                                            const model = e.target.value;
                                            const found = cBrandModels.find((m) => m.model === model);
                                            setCform({ ...cform, model, paper_width: found?.width || cform.paper_width });
                                        }}
                                        data-testid="cprinter-model">
                                        {cBrandModels.map((m) => (
                                            <option key={m.model} value={m.model}>{m.model} · {m.width}mm</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* CONNESSIONE — 3 protocolli */}
                            <div>
                                <label className="block text-[10px] uppercase tracking-wider text-elegant mb-1">Tipo connessione</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {Object.entries(PROTOCOL_META).map(([k, m]) => {
                                        const Icon = m.icon;
                                        const active = cform.protocol === k;
                                        return (
                                            <button key={k} type="button"
                                                onClick={() => setCform({ ...cform, protocol: k })}
                                                data-testid={`cprinter-proto-${k}`}
                                                className={`h-16 rounded-md border-2 flex flex-col items-center justify-center gap-1 text-[10px] uppercase tracking-wider ${
                                                    active ? `border-brand bg-brand-soft text-warm` : "border-[#1f2c44] text-elegant hover:border-brand/50"
                                                }`}>
                                                <Icon className={`w-4 h-4 ${active ? "text-brand" : ""}`} />
                                                {m.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="text-[11px] text-elegant mt-1">{PROTOCOL_META[cform.protocol]?.hint}</div>
                            </div>

                            {/* ----- TCP/LAN fields ----- */}
                            {cform.protocol === "tcp" && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="col-span-2">
                                        <label className="block text-[10px] uppercase tracking-wider text-elegant mb-1">Indirizzo IP <span className="text-amber-400">*</span></label>
                                        <input className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none font-mono"
                                            placeholder="192.168.1.50"
                                            value={cform.ip}
                                            onChange={(e) => setCform({ ...cform, ip: e.target.value })}
                                            data-testid="cprinter-ip" />
                                        <div className="text-[11px] text-elegant mt-1">L'IP della stampante: lo trovi sull'auto-test (premi FEED all'accensione)</div>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] uppercase tracking-wider text-elegant mb-1">Porta</label>
                                        <input className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md font-mono"
                                            placeholder="9100"
                                            value={cform.port}
                                            onChange={(e) => setCform({ ...cform, port: e.target.value })} />
                                    </div>
                                </div>
                            )}

                            {/* ----- USB pairing ----- */}
                            {cform.protocol === "usb" && (
                                <div className="bg-deep border border-purple-500/30 rounded-md p-3">
                                    {cform.usb_vendor_id ? (
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="text-sm">
                                                <div className="text-purple-300 font-bold flex items-center gap-2">
                                                    <CheckCircle2 className="w-4 h-4" /> Pairata in questo browser
                                                </div>
                                                <div className="text-xs text-elegant mt-1">
                                                    {cform.usb_product_name || `VID ${cform.usb_vendor_id.toString(16)} · PID ${cform.usb_product_id.toString(16)}`}
                                                </div>
                                            </div>
                                            <button type="button" onClick={pairUsb} data-testid="cprinter-pair-usb-redo"
                                                className="h-10 px-3 border border-[#1f2c44] hover:border-purple-400 text-elegant hover:text-purple-300 rounded text-xs uppercase">
                                                Cambia
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="text-center">
                                            <Usb className="w-8 h-8 text-purple-300 mx-auto mb-2" />
                                            <div className="text-sm text-warm font-bold mb-1">Collega la stampante USB</div>
                                            <div className="text-xs text-elegant mb-3">
                                                Apri questa pagina nel browser <strong>sul PC dove è collegata la stampante USB</strong>, poi click qui:
                                            </div>
                                            <button type="button" onClick={pairUsb} data-testid="cprinter-pair-usb"
                                                className="h-12 px-6 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded uppercase tracking-wider text-xs flex items-center gap-2 mx-auto">
                                                <Usb className="w-4 h-4" /> Collega stampante USB
                                            </button>
                                            <div className="text-[11px] text-elegant mt-3">
                                                Si aprirà una finestra del browser: seleziona la stampante dalla lista.
                                                <br />Richiede Chrome o Edge. Su Windows alcune stampanti necessitano del driver <strong>WinUSB</strong> (installalo con <a href="https://zadig.akeo.ie/" target="_blank" rel="noopener noreferrer" className="text-purple-300 underline">Zadig</a>).
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ----- Bluetooth pairing ----- */}
                            {cform.protocol === "bluetooth" && (
                                <div className="bg-deep border border-sky-500/30 rounded-md p-3">
                                    {cform.bt_device_id ? (
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="text-sm">
                                                <div className="text-sky-300 font-bold flex items-center gap-2">
                                                    <CheckCircle2 className="w-4 h-4" /> Pairata in questo browser
                                                </div>
                                                <div className="text-xs text-elegant mt-1">
                                                    {cform.bt_device_name || cform.bt_device_id}
                                                </div>
                                            </div>
                                            <button type="button" onClick={pairBt} data-testid="cprinter-pair-bt-redo"
                                                className="h-10 px-3 border border-[#1f2c44] hover:border-sky-400 text-elegant hover:text-sky-300 rounded text-xs uppercase">
                                                Cambia
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="text-center">
                                            <Bluetooth className="w-8 h-8 text-sky-300 mx-auto mb-2" />
                                            <div className="text-sm text-warm font-bold mb-1">Collega la stampante Bluetooth</div>
                                            <div className="text-xs text-elegant mb-3">
                                                Accendi la stampante in modalità accoppiamento, poi click qui:
                                            </div>
                                            <button type="button" onClick={pairBt} data-testid="cprinter-pair-bt"
                                                className="h-12 px-6 bg-sky-600 hover:bg-sky-500 text-white font-bold rounded uppercase tracking-wider text-xs flex items-center gap-2 mx-auto">
                                                <Bluetooth className="w-4 h-4" /> Collega stampante Bluetooth
                                            </button>
                                            <div className="text-[11px] text-elegant mt-3">
                                                Richiede Chrome o Edge (e Bluetooth attivo sul dispositivo).
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div>
                                <label className="block text-[10px] uppercase tracking-wider text-elegant mb-1">Larghezza carta</label>
                                <select className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md"
                                    value={cform.paper_width}
                                    onChange={(e) => setCform({ ...cform, paper_width: parseInt(e.target.value, 10) })}
                                    data-testid="cprinter-width">
                                    <option value={58}>58 mm</option>
                                    <option value={76}>76 mm</option>
                                    <option value={80}>80 mm</option>
                                    <option value={112}>112 mm</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-5">
                            <button onClick={() => { setCshow(false); setEditingId(null); }} data-testid="cprinter-cancel"
                                className="flex-1 h-12 border border-[#1f2c44] rounded-md">Annulla</button>
                            <button onClick={saveCommand} data-testid="cprinter-save"
                                className="flex-1 h-12 bg-brand text-deep font-bold rounded-md">
                                {editingId ? "Salva modifiche" : "Aggiungi stampante"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ============ TAB FISCAL ============ */}
            {tab === "fiscal" && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <p className="text-sm text-elegant">
                            Stampanti telematiche <span className="text-warm font-bold">fiscali</span> per scontrini e chiusure giornaliere.
                        </p>
                        <button onClick={() => setShow(true)} data-testid="add-printer-button"
                            className="h-12 px-5 bg-brand text-deep font-bold rounded-md flex items-center gap-2">
                            <Plus className="w-4 h-4" /> Nuova Fiscale
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {printers.map((p) => (
                            <div key={p.id} className="bg-surface border border-[#1f2c44] rounded-lg p-5" data-testid={`printer-${p.id}`}>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-brand">{p.brand}</div>
                                <div className="font-display text-xl font-bold">{p.model}</div>
                                <div className="text-xs text-elegant mt-1">{p.name}</div>
                                <div className="text-xs mt-3 space-y-1">
                                    <div><span className="text-elegant">IP:</span> {p.ip || "—"}</div>
                                    <div><span className="text-elegant">Porta:</span> {p.port}</div>
                                </div>
                                <div className="flex gap-2 mt-4">
                                    <button onClick={() => testFiscal(p.id)} className="flex-1 h-10 border border-brand text-brand rounded-md text-xs uppercase flex items-center justify-center gap-1">
                                        <Wifi className="w-3 h-3" /> Test
                                    </button>
                                    <button onClick={() => removeFiscal(p.id)} className="w-10 h-10 border border-red-700/40 text-red-400 rounded-md hover:bg-red-900/20"><X className="w-4 h-4 mx-auto" /></button>
                                </div>
                            </div>
                        ))}
                        {printers.length === 0 && <div className="col-span-full bg-surface border border-[#1f2c44] rounded-lg p-8 text-center text-elegant">Nessuna stampante fiscale configurata.</div>}
                    </div>
                </div>
            )}

            {/* MODAL FISCAL */}
            {show && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 max-w-lg w-full">
                        <h2 className="font-display text-2xl font-bold mb-4">Nuova Stampante Fiscale</h2>
                        <div className="space-y-3">
                            <input className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                                placeholder="Nome (es. Cassa Sala)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="printer-name" />
                            <div className="grid grid-cols-2 gap-3">
                                <select className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md"
                                    value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value, model: models.find((m) => m.brand === e.target.value)?.model || "" })} data-testid="printer-brand">
                                    {[...new Set(models.map((m) => m.brand))].map((b) => <option key={b} value={b}>{b}</option>)}
                                </select>
                                <select className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md"
                                    value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="printer-model">
                                    {brandModels.map((m) => <option key={m.model} value={m.model}>{m.model}</option>)}
                                </select>
                                <input className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md" placeholder="IP" value={form.ip} onChange={(e) => setForm({ ...form, ip: e.target.value })} data-testid="printer-ip" />
                                <input className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md" placeholder="Porta" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} />
                                <select className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md col-span-2" value={form.protocol} onChange={(e) => setForm({ ...form, protocol: e.target.value })}>
                                    <option value="tcp">TCP</option>
                                    <option value="usb">USB</option>
                                    <option value="serial">Seriale</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-5">
                            <button onClick={() => setShow(false)} className="flex-1 h-12 border border-[#1f2c44] rounded-md">Annulla</button>
                            <button onClick={createFiscal} data-testid="save-printer-button" className="flex-1 h-12 bg-brand text-deep font-bold rounded-md">Salva</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Stampanti;
